import { Download, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

interface ReportsMastheadProps {
  mastheadVolume: string;
  subtitle?: string;
  title?: React.ReactNode;
  loading: boolean;
  needsCustomRange: boolean;
  isDownloadingPdf: boolean;
  isDownloadingCsv: boolean;
  onDownloadPdf: () => void | Promise<void>;
  onDownloadCsv: () => void | Promise<void>;
  // When `onSendEmail` is omitted, the Send Email button is not rendered.
  // Admin "reviewer preview" uses this to drop the SME-only convenience.
  isSendingEmail?: boolean;
  onSendEmail?: () => void | Promise<void>;
}

const DEFAULT_TITLE = (
  <>
    <span className="italic font-normal">The</span> <span className="font-normal">Reports</span>
  </>
);

const DEFAULT_SUBTITLE =
  'A quiet summary of the books — revenue, expenses, and the slow work of a small studio in its first full year.';

export function ReportsMasthead({
  mastheadVolume,
  subtitle = DEFAULT_SUBTITLE,
  title = DEFAULT_TITLE,
  loading,
  needsCustomRange,
  isDownloadingPdf,
  isDownloadingCsv,
  isSendingEmail,
  onDownloadPdf,
  onDownloadCsv,
  onSendEmail,
}: ReportsMastheadProps) {
  const actionsDisabled = loading || needsCustomRange;

  return (
    <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
      <div className="max-w-160">
        {loading ? (
          <div
            aria-hidden
            className="h-3 w-48 bg-surface-200/80 rounded-sm animate-pulse mb-4"
          />
        ) : (
          <div className="font-mono text-[11px] uppercase tracking-[2px] text-surface-600 mb-4">
            {mastheadVolume}
          </div>
        )}
        <h1 className="font-serif text-[56px] leading-[0.95] tracking-tight text-surface-900">
          {title}
        </h1>
        <p className="mt-5 text-[14px] leading-relaxed text-surface-700 max-w-130">
          {subtitle}
        </p>
      </div>
      <div className="flex gap-2 shrink-0">
        <Button
          variant="outline"
          size="lg"
          onClick={() => void onDownloadPdf()}
          disabled={isDownloadingPdf || actionsDisabled}
          aria-busy={isDownloadingPdf}
        >
          <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
          PDF
        </Button>
        <Button
          variant="outline"
          size="lg"
          onClick={() => void onDownloadCsv()}
          disabled={isDownloadingCsv || actionsDisabled}
          aria-busy={isDownloadingCsv}
        >
          <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
          CSV
        </Button>
        {onSendEmail && (
          <Button
            size="lg"
            onClick={() => void onSendEmail()}
            disabled={isSendingEmail || actionsDisabled}
            aria-busy={isSendingEmail}
          >
            <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
            {isSendingEmail ? 'Sending…' : 'Send Email'}
          </Button>
        )}
      </div>
    </header>
  );
}
